export type CategoryType = 'income' | 'expense' | 'asset' | 'liability' | 'equity';
export type StatementSection = 'profit-loss' | 'balance-sheet';

export interface AccountingCategory {
  id: string;
  ownerId: string;
  nominalCode: string;
  name: string;
  parentCode?: string;
  categoryType: CategoryType;
  statementSection: StatementSection;
  displayOrder: number;
  faCode?: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  vatTreatment?: string;
  depreciationMethod?: string;
  taxTreatment?: string;
  group?: string;
  reportingType?: string;
}

export interface DefaultCategory {
  nominalCode: string;
  name: string;
  parentCode?: string;
  categoryType: CategoryType;
  statementSection: StatementSection;
  displayOrder: number;
  faCode?: string;
  vatTreatment?: string;
  depreciationMethod?: string;
  taxTreatment?: string;
  group?: string;
  reportingType?: string;
}

export const DEFAULT_CATEGORIES: DefaultCategory[] = [
  // ── Income (1000–1999) ──
  { nominalCode: '1000', name: 'Income', categoryType: 'income', statementSection: 'profit-loss', displayOrder: 100 },
  { nominalCode: '1001', name: 'Service Charges', parentCode: '1000', categoryType: 'income', statementSection: 'profit-loss', displayOrder: 110 },
  { nominalCode: '1002', name: 'Sinking Fund Contributions', parentCode: '1000', categoryType: 'income', statementSection: 'profit-loss', displayOrder: 120 },
  { nominalCode: '1003', name: 'Interest Received', parentCode: '1000', categoryType: 'income', statementSection: 'profit-loss', displayOrder: 130, faCode: '051' },
  { nominalCode: '1004', name: 'Trade Debtors', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 140, faCode: '681' },
  { nominalCode: '1005', name: 'Other Income', parentCode: '1000', categoryType: 'income', statementSection: 'profit-loss', displayOrder: 150 },

  // ── Current Assets (1900–1999) — control accounts for double-entry postings ──
  // (accounting-model Requirement 5 / CONTROL_ACCOUNTS in shared/src/accounting)
  { nominalCode: '1900', name: 'Cash at Bank', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 160 },
  { nominalCode: '1910', name: 'Service Charge Debtors', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 170 },

  // ── Admin Expenses (2000–2999) ──
  { nominalCode: '2000', name: 'Admin Expenses', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 200 },
  { nominalCode: '2001', name: 'Management Fees', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 210 },
  { nominalCode: '2002', name: 'Insurance', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 220 },
  { nominalCode: '2003', name: 'Bank Charges', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 230 },
  { nominalCode: '2004', name: 'Accountancy Fees', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 240 },
  { nominalCode: '2005', name: 'Legal Fees', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 250 },
  { nominalCode: '2006', name: 'Postage & Stationery', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 260 },
  { nominalCode: '2007', name: 'Sundry Expenses', parentCode: '2000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 270 },

  // ── Cost of Sales (3000–3999) ──
  { nominalCode: '3000', name: 'Cost of Sales', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 300 },
  { nominalCode: '3001', name: 'Repairs & Maintenance', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 310 },
  { nominalCode: '3002', name: 'Utilities', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 320 },
  { nominalCode: '3003', name: 'Cleaning', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 330 },
  { nominalCode: '3004', name: 'Gardening & Grounds', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 340 },
  { nominalCode: '3005', name: 'Security', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 350 },
  { nominalCode: '3006', name: 'Health & Safety', parentCode: '3000', categoryType: 'expense', statementSection: 'profit-loss', displayOrder: 360 },

  // ── Capital Assets (4000–4999) ──
  { nominalCode: '4000', name: 'Capital Assets', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 400 },
  { nominalCode: '4001', name: 'Fixed Assets', parentCode: '4000', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 410 },
  { nominalCode: '4002', name: 'Capital Improvements', parentCode: '4000', categoryType: 'asset', statementSection: 'balance-sheet', displayOrder: 420 },

  // ── Liabilities (5000–5999) ──
  { nominalCode: '5000', name: 'Liabilities', categoryType: 'liability', statementSection: 'balance-sheet', displayOrder: 500 },
  { nominalCode: '5001', name: 'Trade Creditors', parentCode: '5000', categoryType: 'liability', statementSection: 'balance-sheet', displayOrder: 510, faCode: '796' },
  { nominalCode: '5002', name: 'Prepaid Service Charges', parentCode: '5000', categoryType: 'liability', statementSection: 'balance-sheet', displayOrder: 520 },
  { nominalCode: '5003', name: 'Contra Account', parentCode: '5000', categoryType: 'liability', statementSection: 'balance-sheet', displayOrder: 530, faCode: '998' },

  // ── Equity (6000–6999) ──
  { nominalCode: '6000', name: 'Equity', categoryType: 'equity', statementSection: 'balance-sheet', displayOrder: 600 },
  { nominalCode: '6001', name: 'Retained Surplus', parentCode: '6000', categoryType: 'equity', statementSection: 'balance-sheet', displayOrder: 610 },
  { nominalCode: '6002', name: 'Sinking Fund Reserve', parentCode: '6000', categoryType: 'equity', statementSection: 'balance-sheet', displayOrder: 620 },
];

/** Map FreeAgent nominal codes to internal codes */
export const FA_CODE_MAP: Record<string, string> = Object.fromEntries(
  DEFAULT_CATEGORIES.filter(c => c.faCode).map(c => [c.faCode!, c.nominalCode])
);
